import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import type { LearningPillar } from '../learning-lifecycle/types';
// Reuse the real "Build a program" chat building blocks so the questionnaire
// matches Elliott's build-a-program experience exactly (typewriter + bubbles).
import ElliottMessage, { messageBodySx, AVATAR_GUTTER } from '../learning-lifecycle/add-program-chat/ElliottMessage';
import LearnerMessage from '../learning-lifecycle/add-program-chat/LearnerMessage';
import StreamingText from '../learning-lifecycle/add-program-chat/StreamingText';
import TypingDots from '../learning-lifecycle/add-program-chat/TypingDots';
import ElliottWorking from '../../../../../../components/ui/ElliottWorking';
import InfoTip from '../../../../../../components/ui/InfoTip';
import { isCapabilityRelease } from '../../../../../../utils/capabilityRelease';
import ChatInputBar from '../learning-lifecycle/add-program-chat/ChatInputBar';
import { OptionPill, OptionCard } from '../learning-lifecycle/add-program-chat/OptionPill';
import { buttonDisabledSx } from '../../../../../../components/ui/Buttons/buttonStyles';
import { elliottTokens as et } from '../learning-lifecycle/add-program-chat/tokens';
import {
  FW_PILLARS,
  FW_QUESTIONS,
  FW_TYPE_COLORS,
  FwPillarKey,
  FwProgram,
  FwQuestion,
  isAssessment,
  teamLabel
} from './frameworkData';
import {
  FwAnswers,
  GOLD_TIER_BENEFITS,
  computeRoi,
  currency,
  emptyAnswers,
  tierByKey
} from './frameworkEngine';
import type { AddedLink, LearningFrameworkStore } from './useLearningFrameworkStore';

// Learning Framework Builder. Elliott takes the admin through a short
// questionnaire, then recommends a full Learning Framework rendered like the
// dashboard, with an ROI estimate, an org tier, and add buttons. The
// recommendation comes from the BE (Elliott live, docs/EPIC-learning-framework-
// builder.md) with the built-in engine as the fallback; the framework itself is
// stored per org by useLearningFrameworkStore.

// Site-consistent text tokens (primary white, secondary / tertiary greys).
const TEXT = '#FDFDFD';
const DIM = '#B6B6B6';
const DIMMER = '#868686';
const CARD = '#1E1E1E';
const CARD_BD = '#333333';
const ORANGE = '#F79420';
const ORANGE2 = '#F15A26';
const GREEN = '#4ADE80';
const BRANDY = `linear-gradient(180deg, ${ORANGE} 0%, ${ORANGE2} 100%)`;

type Phase = 'chat' | 'generating' | 'results';
interface Column {
  key: FwPillarKey;
  label: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  // The org's saved framework (BE, local fallback) and the actions on it.
  store: LearningFrameworkStore;
  orgName?: string;
  // The logged-in admin's first name, used to greet them in the chat.
  userName?: string;
  // The org's real pillars, so the result columns use the admin's own names.
  pillars?: LearningPillar[];
  // Adds the chosen programs as Design tiles on the dashboard. Resolves with the
  // recommendation → program links actually created, so only those are marked
  // as added (and the link is recorded on the server).
  onAddPrograms?: (programs: FwProgram[]) => Promise<AddedLink[]>;
}

const primaryBtnSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  px: '18px',
  py: '10px',
  borderRadius: '8px',
  border: 'none',
  background: BRANDY,
  color: '#FFFFFF',
  fontFamily: 'Inter, sans-serif',
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  transition: 'filter 0.15s',
  '&:hover': { filter: 'brightness(1.08)' },
  '&:disabled': { ...buttonDisabledSx, cursor: 'not-allowed' }
} as const;

const ghostBtnSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  px: '14px',
  py: '9px',
  borderRadius: '8px',
  border: '1px solid #2A2A2A',
  bgcolor: '#1C1C1C',
  color: TEXT,
  fontFamily: 'Inter, sans-serif',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  '&:hover': { bgcolor: '#262626' }
} as const;

const LearningFrameworkBuilder: React.FC<Props> = ({ open, onClose, store, orgName, userName, pillars, onAddPrograms }) => {
  const [phase, setPhase] = useState<Phase>('chat');
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<FwAnswers>(emptyAnswers());
  // Programs with a create request in flight: shown as "Adding" and not
  // clickable, so a slow network can't create the same tile twice.
  const [pending, setPending] = useState<Set<string>>(new Set());

  const { framework } = store;
  // Assessments aren't available yet. New frameworks only recommend courses; a
  // framework saved before that can still hold assessments, so they're left out.
  const recommended = useMemo(() => (framework?.programs ?? []).filter((p) => !isAssessment(p)), [framework]);
  const added = useMemo(() => new Set((framework?.added ?? []).map((a) => a.recommendationId)), [framework]);
  const roi = useMemo(() => computeRoi(answers, recommended.length), [answers, recommended.length]);

  // Columns use the org's real pillar names (the admin may have renamed them);
  // fall back to the defaults when pillars haven't loaded.
  const columns: Column[] = useMemo(() => {
    const withIcon = (pillars || []).filter((p) => p.icon);
    if (withIcon.length) {
      return withIcon.map((p) => ({ key: p.icon as FwPillarKey, label: (p.name || '').trim() || defaultLabel(p.icon as FwPillarKey) }));
    }
    return FW_PILLARS.map((p) => ({ key: p.key, label: p.label }));
  }, [pillars]);

  // Opening: a saved framework opens straight on its results; otherwise the
  // questionnaire from the top. Waits for the store's initial load so a slow
  // GET can't drop the admin into the questionnaire they already answered.
  useEffect(() => {
    if (!open || store.loading) return;
    if (store.framework) {
      setAnswers(store.framework.answers);
      setPhase('results');
    } else {
      setPhase('chat');
      setStep(0);
      setAnswers(emptyAnswers());
    }
    // Only re-run when the dialog opens or the initial load settles — the
    // framework changing while open (generate / add) must not reset the view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, store.loading]);

  const startOver = async () => {
    await store.remove();
    setAnswers(emptyAnswers());
    setStep(0);
    setPhase('chat');
  };

  // Generating runs in an effect, not in the click handler: the last answer
  // and the phase change land in the same render, so the effect reads the
  // complete answers where a closure taken in the handler would miss the final
  // question. The ref stops a second run while one is in flight.
  const generate = () => setPhase('generating');
  const generatingRef = useRef(false);
  useEffect(() => {
    if (phase !== 'generating' || generatingRef.current) return;
    generatingRef.current = true;
    let cancelled = false;
    const started = Date.now();
    (async () => {
      try {
        await store.generate(answers);
      } finally {
        generatingRef.current = false;
        // Keep the "Elliott is building…" beat readable when the engine answers instantly.
        const wait = Math.max(0, 1300 - (Date.now() - started));
        window.setTimeout(() => {
          if (!cancelled) setPhase('results');
        }, wait);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const setAnswer = (id: string, value: unknown) => setAnswers((a) => ({ ...a, [id]: value }));

  // Only programs the dashboard confirms were created are marked as added (the
  // store records the recommendation → program link). A failed create keeps
  // its "+ Add" button so the admin can retry.
  const addPrograms = async (progs: FwProgram[]) => {
    const toAdd = progs.filter((p) => !added.has(p.id) && !pending.has(p.id));
    if (!toAdd.length || !onAddPrograms) return;
    const ids = toAdd.map((p) => p.id);
    setPending((prev) => new Set([...Array.from(prev), ...ids]));
    let links: AddedLink[] = [];
    try {
      links = await onAddPrograms(toAdd);
    } finally {
      setPending((prev) => new Set(Array.from(prev).filter((id) => !ids.includes(id))));
    }
    if (links.length) await store.markAdded(links);
  };
  const addProgram = (p: FwProgram) => addPrograms([p]);
  const addAll = () => addPrograms(recommended);

  // The questionnaire is the real "Build a program" experience: a centred Elliott
  // modal (not a black full-screen page) with typed-out questions. The results
  // page needs the full dashboard width, so it opens wider.
  const isResults = phase === 'results';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)'
          }
        }
      }}
      PaperProps={{
        sx: {
          width: isResults ? '96vw' : '100%',
          maxWidth: isResults ? '96vw' : 1040,
          height: isResults ? '94vh' : 'min(88vh, 720px)',
          m: 2,
          bgcolor: et.bg, // #0F0F0F — Fill/Modal/background, matches build-a-program
          backgroundImage: 'none',
          color: TEXT,
          border: '1px solid #444444',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'width 0.2s ease, height 0.2s ease, max-width 0.2s ease'
        }
      }}
    >
      {/* Header - matches the Elliott modal chrome (sparkle + title). */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: '24px',
          py: '20px',
          borderBottom: '1px solid #2A2A2A',
          flexShrink: 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 15, letterSpacing: '0.04em', color: TEXT }}>
            PROGRAM PLAN
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close" sx={{ width: 22, height: 22, p: 0 }}>
          <CloseIcon sx={{ color: TEXT, fontSize: 16 }} />
        </IconButton>
      </Box>

      {phase === 'chat' ? (
        // ChatFlow owns its own scroll area + sticky reply bar, so it fills the
        // modal body directly (no outer padded scroller).
        <ChatFlow userName={userName} answers={answers} setAnswer={setAnswer} step={step} setStep={setStep} onFinish={generate} />
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 2, md: 4 }, py: 4 }}>
          {phase === 'generating' ? (
            <Generating />
          ) : (
            <ResultsView
              columns={columns}
              recommended={recommended}
              roi={roi}
              answers={answers}
              orgName={orgName}
              intro={framework?.intro || ''}
              source={framework?.source || 'engine'}
              storage={store.storage}
              added={added}
              pending={pending}
              onAdd={addProgram}
              onAddAll={addAll}
              onStartOver={startOver}
            />
          )}
        </Box>
      )}
    </Dialog>
  );
};

export default LearningFrameworkBuilder;

const defaultLabel = (key: FwPillarKey) => FW_PILLARS.find((p) => p.key === key)?.label || key.toUpperCase();

// ── Chat questionnaire (Elliott orb style) ───────────────────────────────────
const answerSummary = (q: FwQuestion, value: unknown): string => {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
    return q.optional ? 'Skipped' : '';
  }
  if (q.kind === 'single') {
    const opt = q.options?.find((o) => o.value === value);
    return opt ? opt.label : String(value);
  }
  if (q.kind === 'number' || q.kind === 'text') return String(value);
  if (q.kind === 'multi') {
    return (value as string[]).map((v) => q.options?.find((o) => o.value === v)?.label || v).join(', ');
  }
  if (q.kind === 'grouped-multi') {
    const all = (q.groups || []).flatMap((g) => g.options);
    return (value as string[]).map((v) => all.find((o) => o.value === v)?.label || v).join(', ');
  }
  return String(value);
};

// A committed turn in the questionnaire thread (already typed, no re-animation).
interface Turn {
  id: string;
  role: 'elliott' | 'learner';
  text: string;
}

// Elliott's greeting is folded into the first question so the whole thing types
// out as one message (matching how build-a-program opens).
const greeting = (userName?: string) =>
  `Hi${
    userName ? ` ${userName}` : ''
  }, let's build your Program Plan. I'll ask a few quick questions, then recommend the programs your people need. It takes about two minutes.`;

const ChatFlow: React.FC<{
  userName?: string;
  answers: FwAnswers;
  setAnswer: (id: string, value: unknown) => void;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  onFinish: () => void;
}> = ({ userName, answers, setAnswer, step, setStep, onFinish }) => {
  const total = FW_QUESTIONS.length;
  const q = FW_QUESTIONS[step];
  const value = (answers as unknown as Record<string, unknown>)[q.id];

  const [thread, setThread] = useState<Turn[]>([]);
  const [typing, setTyping] = useState(true); // brief "typing" beat before Elliott streams
  const [promptStreamed, setPromptStreamed] = useState(false);
  const [draft, setDraft] = useState('');
  const endRef = useRef<HTMLDivElement | null>(null);

  // The Elliott copy for the current step (greeting prepended on the first).
  const promptText = step === 0 ? `${greeting(userName)}\n\n${q.title}` : q.title;

  // Each new question: a short typing beat, then the copy streams in.
  useEffect(() => {
    setPromptStreamed(false);
    setTyping(true);
    const id = window.setTimeout(() => setTyping(false), 450);
    return () => window.clearTimeout(id);
  }, [step]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [thread.length, typing, promptStreamed, step]);

  // Commit the current Q + the admin's answer to history, then advance (or finish).
  const advance = (summary: string) => {
    setThread((prev) => [
      ...prev,
      { id: `${q.id}-q`, role: 'elliott', text: promptText },
      { id: `${q.id}-a`, role: 'learner', text: summary || 'Skipped' }
    ]);
    setDraft('');
    if (step >= total - 1) onFinish();
    else setStep((s) => s + 1);
  };

  const arr = Array.isArray(value) ? (value as string[]) : [];
  const toggle = (v: string) => setAnswer(q.id, arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const multiAnswered = arr.length > 0 || Boolean(q.optional);

  // The persistent reply bar is active only on typed answers (number / free text
  // / "Other" on a single-select); selection steps keep it read-only, so the
  // highlight moves to the option cards instead — exactly like build-a-program.
  // Multi / grouped-multi with allowOther: the reply bar adds a custom value to
  // the selection (via the Elliott chat input), then Continue advances.
  const isMultiOther = (q.kind === 'multi' || q.kind === 'grouped-multi') && Boolean(q.allowOther);
  const isFreeText = q.kind === 'number' || q.kind === 'text' || (q.kind === 'single' && Boolean(q.allowOther));
  const canType = isFreeText || isMultiOther;
  const draftValid = q.kind === 'number' ? draft.trim() !== '' && !Number.isNaN(Number(draft)) : draft.trim() !== '';

  // Selected values the admin typed in that aren't in our preset options.
  const knownValues =
    q.kind === 'grouped-multi'
      ? (q.groups || []).flatMap((g) => g.options.map((o) => o.value))
      : (q.options || []).map((o) => o.value);
  const customValues =
    q.kind === 'multi' || q.kind === 'grouped-multi' ? arr.filter((v) => !knownValues.includes(v)) : [];

  const placeholder =
    q.kind === 'number'
      ? q.placeholder || 'Type a number'
      : q.kind === 'single' && q.allowOther
      ? 'Or type your own...'
      : q.kind === 'text'
      ? q.placeholder || 'Type your answer'
      : isMultiOther
      ? 'Add another, then press enter'
      : 'Select an option above';

  const handleSend = () => {
    if (!canType) return;
    const text = draft.trim();
    if (!text) return;
    if (isMultiOther) {
      if (!arr.includes(text)) setAnswer(q.id, [...arr, text]);
      setDraft('');
      return;
    }
    if (q.kind === 'number') {
      const n = Number(text);
      if (Number.isNaN(n)) return;
      setAnswer(q.id, n);
      advance(String(n));
    } else {
      setAnswer(q.id, text);
      advance(text);
    }
  };

  const indentSx = { pl: `${AVATAR_GUTTER}px` } as const;

  const renderControls = () => (
    <Box sx={{ ...indentSx, display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Typography sx={{ fontSize: 11, color: et.textTertiary, letterSpacing: '0.04em' }}>
        Question {step + 1} of {total}
      </Typography>
      {q.helper && (
        <Typography sx={{ fontSize: 12.5, color: et.textSecondary, lineHeight: 1.5, mt: '-4px' }}>{q.helper}</Typography>
      )}

      {q.kind === 'single' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {q.options?.map((o) => (
            <OptionCard
              key={o.value}
              label={o.label}
              selected={value === o.value}
              onClick={() => {
                setAnswer(q.id, o.value);
                advance(o.label);
              }}
            />
          ))}
        </Box>
      )}

      {q.kind === 'multi' && (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {q.options?.map((o) => (
              <OptionPill key={o.value} label={o.label} selected={arr.includes(o.value)} onClick={() => toggle(o.value)} />
            ))}
            {customValues.map((v) => (
              <OptionPill key={v} label={v} selected onClick={() => toggle(v)} />
            ))}
          </Box>
          <Box sx={{ display: 'flex', mt: '2px' }}>
            <OptionPill
              label={step >= total - 1 ? 'See My Program Plan' : 'Continue'}
              variant="cta"
              disabled={!multiAnswered}
              onClick={() => advance(answerSummary(q, arr))}
            />
          </Box>
        </>
      )}

      {q.kind === 'grouped-multi' && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {q.groups?.map((g) => (
              <Box key={g.label}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: et.textTertiary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    mb: '8px'
                  }}
                >
                  {g.label}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {g.options.map((o) => (
                    <OptionPill key={o.value} label={o.label} selected={arr.includes(o.value)} onClick={() => toggle(o.value)} />
                  ))}
                </Box>
              </Box>
            ))}
            {customValues.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: et.textTertiary, textTransform: 'uppercase', letterSpacing: '0.06em', mb: '8px' }}>
                  Added
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {customValues.map((v) => (
                    <OptionPill key={v} label={v} selected onClick={() => toggle(v)} />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', mt: '2px' }}>
            <OptionPill
              label={step >= total - 1 ? 'See My Program Plan' : 'Continue'}
              variant="cta"
              disabled={!multiAnswered}
              onClick={() => advance(answerSummary(q, arr))}
            />
          </Box>
        </>
      )}

      {(q.kind === 'text' || q.kind === 'number') && q.optional && (
        <Box sx={{ display: 'flex', mt: '2px' }}>
          <OptionPill
            label={step >= total - 1 ? 'Skip and See My Program Plan' : 'Skip'}
            onClick={() => advance('Skipped')}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: '24px', py: '24px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', width: '100%', maxWidth: 640, mx: 'auto' }}>
          {/* History — already typed, rendered instantly */}
          {thread.map((turn) =>
            turn.role === 'elliott' ? (
              <ElliottMessage key={turn.id}>
                <Typography sx={messageBodySx}>{turn.text}</Typography>
              </ElliottMessage>
            ) : (
              <LearnerMessage key={turn.id}>{turn.text}</LearnerMessage>
            )
          )}

          {/* Active question — typing dots, then streaming copy */}
          <ElliottMessage>
            {typing ? (
              <TypingDots />
            ) : (
              <StreamingText
                key={step}
                paragraphs={promptText.split('\n\n')}
                sx={messageBodySx}
                onDone={() => setPromptStreamed(true)}
              />
            )}
          </ElliottMessage>

          {!typing && promptStreamed && renderControls()}

          <Box ref={endRef} />
        </Box>
      </Box>

      {/* Sticky reply bar — active only on typed-answer steps */}
      <Box sx={{ flexShrink: 0, px: '24px', py: '16px', borderTop: `1px solid ${et.borderDim}` }}>
        <Box sx={{ width: '100%', maxWidth: 640, mx: 'auto' }}>
          <ChatInputBar
            value={draft}
            onChange={setDraft}
            onSend={handleSend}
            canSend={canType && draftValid}
            readOnly={!canType}
            placeholder={placeholder}
          />
        </Box>
      </Box>
    </Box>
  );
};

// ── Generating ───────────────────────────────────────────────────────────────
// The pulsing AI sparkle Elliott shows wherever it is generating (Build with
// Elliott's content step uses the same overlay).
const Generating: React.FC = () => (
  <Box sx={{ height: '100%', display: 'flex' }}>
    <ElliottWorking size="full" message="Elliott is building your Program Plan..." />
  </Box>
);

// ── Results ──────────────────────────────────────────────────────────────────

// A short, personalised intro that reflects the admin's own answers back to them,
// so the framework reads as tailored (not a generic template or a random number).
const AI_PHRASE: Record<string, string> = {
  none: 'just getting started with AI',
  basic: 'using AI informally today',
  adopting: 'actively adopting AI across teams',
  advanced: 'with AI embedded in your workflows'
};
const optionLabel = (id: string, value?: string): string => {
  if (!value) return '';
  const q = FW_QUESTIONS.find((x) => x.id === id);
  return q?.options?.find((o) => o.value === value)?.label || value;
};
const buildSummary = (a: FwAnswers, orgName?: string): string => {
  const company = (orgName || '').trim() || 'your company';
  const teams = (a.functions || []).map(teamLabel);
  const teamsPhrase =
    teams.length === 0
      ? ''
      : teams.length <= 3
      ? teams.join(', ')
      : `${teams.slice(0, 3).join(', ')} and ${teams.length - 3} more`;
  const industry = optionLabel('industry', a.industry);
  const clauses: string[] = [];
  if (industry) clauses.push(`a ${industry.toLowerCase()} business`);
  if (a.employees) clauses.push(`${a.employees.toLocaleString('en-US')} people`);
  if (teamsPhrase) clauses.push(`teams across ${teamsPhrase}`);
  if (a.aiCapability && AI_PHRASE[a.aiCapability]) clauses.push(AI_PHRASE[a.aiCapability]);
  const hireNext = optionLabel('hireNext', a.hireNext);
  if (hireNext) clauses.push(`hiring ${hireNext.toLowerCase()} in the next year`);
  const detail = clauses.length ? `: ${clauses.join(', ')}` : '';
  return `This Program Plan is built specifically for ${company}, based on the information you provided${detail}. It's designed to lift your people's effectiveness and reduce your company's risk.`;
};

const ResultsView: React.FC<{
  columns: Column[];
  recommended: FwProgram[];
  roi: ReturnType<typeof computeRoi>;
  answers: FwAnswers;
  orgName?: string;
  // Elliott's own intro when the BE answered; '' falls back to buildSummary.
  intro: string;
  source: 'elliott' | 'engine';
  storage: 'server' | 'local';
  added: Set<string>;
  pending: Set<string>;
  onAdd: (p: FwProgram) => void;
  onAddAll: () => void;
  onStartOver: () => void;
}> = ({ columns, recommended, roi, answers, orgName, intro, source, storage, added, pending, onAdd, onAddAll, onStartOver }) => {
  const allAdded = recommended.length > 0 && recommended.every((p) => added.has(p.id));
  const adding = pending.size > 0;
  // Learning Tier: we don't estimate a "current" tier. We show the tier the org
  // can reach by adopting this framework - always Gold (no Platinum at org level).
  const potential = tierByKey('gold');

  // Programs whose pillar key isn't among the org's columns fall into the first
  // column so nothing is dropped.
  const columnKeys = new Set(columns.map((c) => c.key));
  const leftovers = recommended.filter((p) => !columnKeys.has(p.pillar));

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 22, fontWeight: 700, color: TEXT }}>
            Your recommended Program Plan
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: DIM, mt: 0.5, maxWidth: 680, lineHeight: 1.55 }}>
            {intro || buildSummary(answers, orgName)}
          </Typography>
          {(source === 'engine' || storage === 'local') && (
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: DIMMER, mt: 0.75, maxWidth: 680, lineHeight: 1.5 }}>
              {source === 'engine'
                ? "Elliott's live recommendation wasn't available, so this Program Plan comes from Dosen's built-in catalog. "
                : ''}
              {storage === 'local' ? 'Saved in this browser only until the server is reachable.' : ''}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box component="button" onClick={onStartOver} sx={ghostBtnSx}>
            Start Over
          </Box>
          <Box component="button" onClick={onAddAll} disabled={allAdded || adding} sx={{ ...primaryBtnSx, whiteSpace: 'nowrap' }}>
            {allAdded ? 'All Added' : adding ? 'Adding...' : `Add All ${recommended.length} Programs`}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 4 }}>
        <RoiCard roi={roi} />
        <TierCard potential={potential} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: `repeat(${Math.max(columns.length, 1)}, 1fr)` }, gap: 2 }}>
        {columns.map((col, i) => {
          const items = recommended.filter((p) => p.pillar === col.key).concat(i === 0 ? leftovers : []);
          return (
            <Box key={`${col.key}-${i}`} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 1.25, borderRadius: '8px', border: '1px solid #232323', bgcolor: '#141414' }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', color: DIM, textTransform: 'uppercase' }}>
                  {col.label}
                </Typography>
                <Box sx={{ px: '7px', py: '1px', borderRadius: '4px', bgcolor: '#2A2A2A' }}>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: DIMMER }}>{items.length}</Typography>
                </Box>
              </Box>
              {items.map((p) => (
                <ProgramMiniCard key={p.id} program={p} added={added.has(p.id)} pending={pending.has(p.id)} onAdd={() => onAdd(p)} />
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// Consistent inline "LABEL value" stat (used for Hours and ROI everywhere).
const StatInline: React.FC<{ label: string; value: string; valueColor?: string }> = ({ label, value, valueColor }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: '6px' }}>
    <Typography component="span" sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: DIMMER }}>
      {label}
    </Typography>
    <Typography component="span" sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 700, color: valueColor || TEXT }}>
      {value}
    </Typography>
  </Box>
);

const RoiCard: React.FC<{ roi: ReturnType<typeof computeRoi> }> = ({ roi }) => {
  const total = roi.breakdowns.reduce((s, b) => s + b.value, 0) || 1;
  return (
    <Box sx={{ bgcolor: CARD, border: `1px solid ${CARD_BD}`, borderRadius: '12px', p: 3 }}>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, color: TEXT }}>
        Estimated impact
      </Typography>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 44, fontWeight: 800, color: TEXT, lineHeight: 1.1, mt: 0.5 }}>
        {currency(roi.potentialValue)}
      </Typography>
      <Box sx={{ display: 'flex', gap: 3, mt: 1.25 }}>
        <StatInline label="Hours" value={roi.totalHours.toLocaleString('en-US')} />
        <StatInline label="ROI" value={`${roi.roiPct.toLocaleString('en-US')}%`} valueColor={GREEN} />
      </Box>

      <Box sx={{ display: 'flex', gap: '4px', mt: 2.5, mb: 3 }}>
        {roi.breakdowns.map((b) => (
          <Box key={b.label} sx={{ height: 16, borderRadius: '4px', bgcolor: b.color, flex: b.value / total }} />
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
        {roi.breakdowns.map((b) => (
          <Box key={b.label} sx={{ borderTop: `1px solid ${CARD_BD}`, pt: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600, color: b.color }}>{b.label}</Typography>
              <InfoTip title={b.detail} />
            </Box>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 24, fontWeight: 700, color: TEXT, mt: 0.25, mb: 0.5 }}>
              {currency(b.value)}
            </Typography>
            {b.hours != null && <StatInline label="Hours" value={b.hours.toLocaleString('en-US')} />}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const TierCard: React.FC<{
  potential: { label: string; color: string };
}> = ({ potential }) => (
  <Box sx={{ bgcolor: CARD, border: `1px solid ${CARD_BD}`, borderRadius: '12px', p: 3, display: 'flex', flexDirection: 'column' }}>
    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, color: TEXT }}>
      Measuring impact
    </Typography>
    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: DIM, mt: 0.5, mb: 2.5, lineHeight: 1.5 }}>
      {/* The Activity names with the Capability release (utils/capabilityRelease.ts). */}
      {isCapabilityRelease()
        ? 'Your Activity reflects the learning maturity of your organization. The Activity bands follow the Bersin-style L&D maturity model: each step up tracks with measurably lower churn, faster onboarding, and lower risk.'
        : 'Your Learning Tier reflects the learning maturity of your organization. The tiers follow the Bersin-style L&D maturity model: each step up tracks with measurably lower churn, faster onboarding, and lower risk.'}
    </Typography>
    <TierPill tier={potential} caption="" />
    {/* Benefits of reaching this tier, so the tier means something concrete. */}
    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${CARD_BD}`, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, color: TEXT }}>
        What {potential.label} unlocks
      </Typography>
      {GOLD_TIER_BENEFITS.map((benefit) => (
        <Box key={benefit} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <CheckIcon sx={{ fontSize: 15, color: potential.color, mt: '1px', flex: 'none' }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12.5, color: DIM, lineHeight: 1.4 }}>
            {benefit}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const TierPill: React.FC<{ tier: { label: string; color: string }; caption: string }> = ({ tier, caption }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start' }}>
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        px: '12px',
        py: '6px',
        borderRadius: '999px',
        border: `1px solid ${tier.color}`,
        bgcolor: `${tier.color}1F`
      }}
    >
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: tier.color }} />
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 700, color: tier.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {tier.label}
      </Typography>
    </Box>
    {caption && (
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: DIMMER }}>{caption}</Typography>
    )}
  </Box>
);

// Fixed-height tile so every card matches, regardless of title/description length.
const ProgramMiniCard: React.FC<{ program: FwProgram; added: boolean; pending: boolean; onAdd: () => void }> = ({ program, added, pending, onAdd }) => (
  <Box sx={{ height: 176, bgcolor: CARD, border: `1px solid ${CARD_BD}`, borderRadius: '6px', boxShadow: '0 6px 16px -8px rgba(0,0,0,0.85)', p: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: FW_TYPE_COLORS[program.type], flex: 'none', mt: '4px' }} />
      <Typography sx={{ flex: 1, minWidth: 0, fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600, color: TEXT, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {program.name}
      </Typography>
      {/* Why this program was recommended for the company. */}
      {program.basis && (
        <InfoTip title={program.basis} sx={{ mt: '2px' }} />
      )}
    </Box>
    {/* What the program should include. The tile clamps to three lines; the full
        2-4 sentence description is one hover away. */}
    <Tooltip title={program.description} placement="top" enterDelay={400}>
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 12,
          color: DIM,
          lineHeight: 1.45,
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          cursor: 'default'
        }}
      >
        {program.description}
      </Typography>
    </Tooltip>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 'auto' }}>
      {/* Every recommendation is a course while Assessments aren't available. */}
      <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '8px', py: '3px', borderRadius: '999px', bgcolor: 'transparent', border: `1px solid ${CARD_BD}`, flex: 'none' }}>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 10.5, fontWeight: 600, color: DIMMER }}>
          Training
        </Typography>
      </Box>
      {pending ? (
        // Create request in flight: muted and non-clickable until it settles.
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', px: '10px', py: '5px', borderRadius: '6px', border: `1px solid ${CARD_BD}`, bgcolor: 'rgba(255,255,255,0.04)', color: DIMMER, fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 700, cursor: 'default' }}>
          Adding...
        </Box>
      ) : added ? (
        // Clearly non-clickable once added: muted, outlined, default cursor.
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', px: '10px', py: '5px', borderRadius: '6px', border: `1px solid ${CARD_BD}`, bgcolor: 'rgba(255,255,255,0.04)', color: DIMMER, fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 700, cursor: 'default' }}>
          <CheckIcon sx={{ fontSize: 15 }} /> Added
        </Box>
      ) : (
        <Box
          component="button"
          onClick={onAdd}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            px: '10px',
            py: '5px',
            borderRadius: '6px',
            border: `1px solid ${ORANGE}`,
            bgcolor: 'transparent',
            color: ORANGE,
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'background-color 0.15s',
            '&:hover': { bgcolor: 'rgba(247,148,32,0.12)' }
          }}
        >
          Add
        </Box>
      )}
    </Box>
  </Box>
);
